import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import "./GetAppShared.css";

import StoreBadges from "../../components/StoreBadges";
import { track, getCommonContext } from "../../utils/track";

import logoIcon from "../../assets/t_red.png";

import shotHome from "../../assets/screenshots/home.png";
import shotDetails from "../../assets/screenshots/details.png";
import shotAdd from "../../assets/screenshots/add.png";
import shotList from "../../assets/screenshots/list.png";
import shotReports from "../../assets/screenshots/reports.png";

function isDesktopViewport() {
  return window.matchMedia && window.matchMedia("(min-width: 981px)").matches;
}

const GetAppAdsPage = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const navigate = useNavigate();

  // Ad-specific copy from URL (custom ad stuff)
  const headline =
    params.get("h") || "Easy cattle record keeping from the pasture to your reports.";
  const subhead =
    params.get("s") || "Log fast, stay organized, and make better decisions with clear herd insights.";

  const [desktopViewport, setDesktopViewport] = useState(() => isDesktopViewport());

  const initialShot = params.get("shot") || "details";
  const [shotKey, setShotKey] = useState(initialShot);

  const shots = {
    home: { label: "Dashboard", img: shotHome, alt: "Toro dashboard screen" },
    details: { label: "Animal details", img: shotDetails, alt: "Toro livestock details screen" },
    add: { label: "Quick add", img: shotAdd, alt: "Toro add livestock screen" },
    list: { label: "List", img: shotList, alt: "Toro livestock list screen" },
    reports: { label: "Reports", img: shotReports, alt: "Toro herd reports screen" },
  };

  const activeShot = shots[shotKey] || shots.details;

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("shot", shotKey);
      window.history.replaceState({}, "", url.toString());
    } catch {
      // no-op
    }
  }, [shotKey]);

  useEffect(() => {
    const onResize = () => setDesktopViewport(isDesktopViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    track(
      "page_view_get_app_ads",
      getCommonContext({
        device_type: desktopViewport ? "desktop" : "mobile",
        shot: shotKey,
        variant: "ads",
      }),
      { silent: true }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- page view once on mount only
  }, []);

  // Desktop layout (matches GetAppPage structure)
  if (desktopViewport) {
    return (
      <div className="getapp">
        <header className="getapp-headerDesktop">
          <img
            src={logoIcon}
            alt="Toro"
            className="getapp-logo-img"
            onClick={() => navigate("/")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/")}
          />
        </header>

        <main className="getapp-containerDesktop">
          <section className="getapp-desktopHero">
            <div className="getapp-desktopLeft">
              <h1 className="getapp-titleOpt">{headline}</h1>

              <p className="getapp-subtitleOpt">{subhead}</p>

              <div className="getapp-desktopCtas">
                <StoreBadges eventCategory="get_app_ads" params={params} showToggleOnMobile={true} />
              </div>

              <ul className="getapp-bulletsOpt getapp-bulletsDesktop">
                <li>Quick adds and structured fields for consistent data</li>
                <li>Instant lookup for any animal record</li>
                <li>Simple herd reporting when you need answers</li>
              </ul>

              <div className="getapp-trustOpt">
                <span className="getapp-pillOpt">Fast in the field</span>
                <span className="getapp-pillOpt">Full animal history</span>
                <span className="getapp-pillOpt">Herd insights</span>
              </div>
            </div>

            <div className="getapp-desktopRight">
              <div className="getapp-phoneCard">
                <img className="getapp-heroShot" src={activeShot.img} alt={activeShot.alt} />

                <div className="getapp-shotPickerWrap">
                  <div className="getapp-shotPickerLabel">Preview screens</div>
                  <div className="getapp-shotPicker" role="tablist" aria-label="Choose a screenshot">
                    {Object.entries(shots).map(([key, s]) => (
                      <button
                        key={key}
                        type="button"
                        className={`getapp-shotTab ${shotKey === key ? "isActive" : ""}`}
                        onClick={() => setShotKey(key)}
                        aria-pressed={shotKey === key}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer transparent />
      </div>
    );
  }

  // Mobile layout (matches GetAppPage structure)
  return (
    <div className="getapp">
      <header className="getapp-headerMinimal">
        <img
          src={logoIcon}
          alt="Toro"
          className="getapp-logo-img"
          onClick={() => navigate("/")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && navigate("/")}
        />
      </header>

      <main className="getapp-containerMobile">
        <section className="getapp-heroMobile">
          <h1 className="getapp-titleMobile">{headline}</h1>

          <p className="getapp-subtitleMobile">{subhead}</p>

          <div className="getapp-primaryCta">
            <StoreBadges eventCategory="get_app_ads" params={params} showToggleOnMobile={true} />
          </div>

          <div className="getapp-phoneWrap">
            <img
              src={activeShot.img}
              alt={activeShot.alt}
              className="getapp-phoneShot"
            />
          </div>

          <ul className="getapp-bulletsMobile">
            <li>Fast, structured livestock records</li>
            <li>Every animal, full history</li>
            <li>Clear herd insights at a glance</li>
          </ul>
        </section>
      </main>

      <Footer transparent />
    </div>
  );
};

export default GetAppAdsPage;
